<template>
  <tr :class="{ 'highlighted-file': isHighlighted }" :data-locator="getItemLocator(document)" :data-name="document.basename">
    <td style="padding-left: 9px;">
      <v-checkbox-btn
        v-if="!document.specialFolder"
        class="select-row-checkbox"
        hide-details
        color="primary"
        :model-value="isDocumentSelected"
        :ripple="false"
        data-locator="select-checkbox"
        @update:model-value="selectDocument"
      />
    </td>

    <td>
      <DocumentsTableItemFolderName v-if="document.type === 'folder'" :folder="document" />
      <DocumentsTableItemFileName v-else-if="document.type === 'file'" :file="document" />
    </td>

    <td>
      <app-filesize-display :value="document.size" data-locator="file-size"/>
    </td>

    <td>
      <span
        v-if="document.type === 'file'"
        class="text--small text--regular"
        data-locator="update-date"
      >
        {{ updateDate }}
      </span>
    </td>

    <td data-locator="update-user">
      {{ document.updateUser }}
    </td>

    <td>
      <RightsDisplayButton
        v-if="document.rights"
        :rights="document.rights"
        :isClickable="document.canShare && isCurrentUserPm"
        :disableButton="!isCurrentUserPm"
        style="max-width: 150px"
        @click="openDocumentSharePane"
      />
    </td>

    <td v-if="withLocation">
      <DocumentLocationLink :document="document"
                            @openInFolder="openInFolder"
      />
    </td>

    <td>
      <DocumentsTableItemOptions :document="document"
                                 @openInFolder="openInFolder"
      />
    </td>
  </tr>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import RightsDisplayButton from '@/common/users/RightsDisplayButton.vue'
import { ISOToShortenedDate } from '@/common/utils/dates'
import { toast } from '@/common/utils/toast'
import { ROOT_FOLDER } from '@/project/documents/constants/special-folders'
import DocumentLocationLink from '@/project/documents/DocumentLocationLink.vue'
import DocumentsTableItemFileName from '@/project/documents/table/DocumentsTableItemFileName.vue'
import DocumentsTableItemFolderName from '@/project/documents/table/DocumentsTableItemFolderName.vue'
import DocumentsTableItemOptions from '@/project/documents/table/DocumentsTableItemOptions.vue'
import {
  DELETE_SEARCH_RESULTS,
  GET_FOLDER_PATH,
  GO_TO_FOLDER_PAGINATED,
  LOAD_FOLDERS_TREE,
  SET_TABLE_DOCUMENTS_PANE,
} from '@/store/modules/documents/action_types'
import { DocumentsBreadcrumbActionTypes } from '@/store/modules/documents-breadcrumb/action_types'

export default {
  name: 'DocumentsTableItem',
  components: {
    DocumentsTableItemOptions,
    DocumentLocationLink,
    RightsDisplayButton,
    DocumentsTableItemFileName,
    DocumentsTableItemFolderName,
  },
  emits: ['select'],
  props: {
    document: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      isHighlighted: false,
    }
  },
  computed: {
    ...mapState('documents', ['searchModeEnabled', 'selectedDocuments']),
    ...mapGetters('documents', ['isRecentFolder']),
    ...mapGetters('room', ['isCurrentUserPm']),
    isDocumentSelected () {
      return !!this.selectedDocuments.find(selectedDocument => selectedDocument.id === this.document.id)
    },
    updateDate () {
      return ISOToShortenedDate(this.document.updateTime)
    },
    withLocation () {
      return this.searchModeEnabled || this.isRecentFolder
    },
  },
  methods: {
    ...mapActions('documents', [
      DELETE_SEARCH_RESULTS,
      GO_TO_FOLDER_PAGINATED,
      GET_FOLDER_PATH,
      SET_TABLE_DOCUMENTS_PANE,
      LOAD_FOLDERS_TREE,
    ]),
    ...mapActions('documentsBreadcrumb', [DocumentsBreadcrumbActionTypes.SET_BREADCRUMB_ITEMS]),
    async getFolderById (folderId) {
      try {
        await this.GO_TO_FOLDER_PAGINATED(folderId)
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.documents.table.DocumentsTableItem.getDocumentsError'))
      }
    },
    async getParentPath () {
      try {
        return await this.GET_FOLDER_PATH(this.document.parent.id)
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.documents.table.DocumentsTableItem.getFolderPathError'))
      }
    },
    openDocumentSharePane () {
      this.SET_TABLE_DOCUMENTS_PANE({
        isOpen: true,
        documents: [this.document],
        tab: 'share',
        forcePmSelection: true,
      })
    },
    async openInFolder () {
      const rootFolderBreadcrumbItem = {
        id: ROOT_FOLDER.id,
        name: this.$t('common.documents.rootFolder'),
      }

      if (this.searchModeEnabled) {
        this.DELETE_SEARCH_RESULTS()
      }

      if (this.document.parent) {
        this.LOAD_FOLDERS_TREE(this.document.parent.id)
        this.getFolderById(this.document.parent.id)

        const parentPath = await this.getParentPath()
        this.SET_BREADCRUMB_ITEMS([rootFolderBreadcrumbItem].concat(parentPath))

      } else {
        await this.getFolderById(ROOT_FOLDER.id)
        this.SET_BREADCRUMB_ITEMS([rootFolderBreadcrumbItem])
      }
    },
    selectDocument () {
      this.$emit('select')
    },
    getItemLocator (item) {
      const specialFoldersLocators = {
        new: 'recents-folder-row',
        todo: 'checklist-attachments-folder-row',
      };

      if (item.specialFolder && specialFoldersLocators[item.specialFolder]) {
        return specialFoldersLocators[item.specialFolder];
      }

      return `documents-${item.type}-row`;
    },
  },
  mounted() {
    if (this.$route.query.displayedFile && this.$route.query.displayedFolder) {
      const displayedFileId = parseInt(this.$route.query.displayedFile)
      if (this.document.id === displayedFileId) {
        this.isHighlighted = true
        this.$router.replace({ query: null });
      }
    }
  },
}
</script>

<style scoped lang="scss">
.select-row-checkbox {
  :deep(.v-selection-control__input > i > svg) {
    width: 18px;
    height: 18px;
  }
}

.highlighted-file {
  background-color: #e9f1fe;
}
</style>
